import _ from 'lodash';
import React from 'react';
import {
    DocumentNode,
    LazyQueryHookOptions,
    LazyQueryResult,
    MutationFunctionOptions,
    MutationTuple,
    OperationVariables,
    QueryHookOptions,
    QueryResult,
    QueryTuple,
    useApolloClient,
    useQuery,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { getSession } from './stateful-service-hooks.util';

export { getSession, TEST_SESSION_ID } from './stateful-service-hooks.util';

/**
 * Convenience wrapper over Mutations to be done within a Stateful service Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 *
 * @param sessionId
 * @param mutation
 * @param options
 */
export function useStatefulMutation<TData, TVariables>(
    mutationHookResult: MutationTuple<TData, TVariables>,
    sessionId: string,
    sessionTypename: string,
    sessionKeyQuery: DocumentNode,
    nestedSessionKeys: string[] = [],
): MutationTuple<TData, Omit<TVariables, 'session'>> {
    const client = useApolloClient();
    const [mutate, state] = mutationHookResult;

    const mutateWithSession = React.useCallback(
        (
            opts: MutationFunctionOptions<
                TData,
                Omit<TVariables, 'session'>
            > = {},
        ) => {
            const session = getSession(
                client,
                sessionId,
                sessionTypename,
                sessionKeyQuery,
                nestedSessionKeys,
            );

            const variables = (_.merge({}, opts.variables, {
                session,
            }) as unknown) as TVariables;

            return mutate({
                ...opts,
                variables,
            });
        },
        [mutate, sessionId],
    );

    return [mutateWithSession, state];
}

/**
 * Convenience wrapper over Queries to be done within a Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 *
 * @param sessionId
 * @param query
 * @param options
 */
export function useStatefulQuery<TData, TVariables extends OperationVariables>(
    sessionId: string,
    sessionTypename: string,
    sessionKeyQuery: DocumentNode,
    query: DocumentNode,
    options: QueryHookOptions<TData, Omit<TVariables, 'session'>> = {},
    nestedSessionKeys: string[] = [],
    deadline?: number,
    shouldUseApolloQuery = false,
): QueryResult<TData, TVariables> {
    const client = useApolloClient();

    const session = getSession(
        client,
        sessionId,
        sessionTypename,
        sessionKeyQuery,
        nestedSessionKeys,
    );

    // We omitted the session before, but now we are adding it in, so adjust the type
    const variables = (_.merge({}, options.variables, {
        deadline,
        session,
    }) as unknown) as TVariables;

    return useQuery<TData, TVariables>(
        query,
        {
            ...options,
            variables,
        } as QueryHookOptions<TData, TVariables>,
        shouldUseApolloQuery,
    ) as QueryResult<TData, TVariables>;
}

/**
 * Convenience wrapper over Lazy Queries to be done within a Bach Session.
 * This hook will automatically put the current 'genNo' from the cache given
 * a sessionId.
 *
 * @param sessionId
 * @param query
 * @param options
 */
export function useStatefulLazyQuery<
    TData,
    TVariables extends OperationVariables
>(
    queryHookResult: QueryTuple<TData, TVariables>,
    sessionId: string,
    sessionTypename: string,
    sessionKeyQuery: DocumentNode,
    nestedSessionKeys: string[] = [],
): QueryTuple<TData, TVariables> {
    const client = useApolloClient();

    const [lazyQuery, state] = queryHookResult;
    const { refetch, ...restState } = state;

    const lazyQueryWithSession = React.useCallback(
        (
            opts: Partial<
                Omit<LazyQueryHookOptions<TData, TVariables>, 'variables'>
            > & {
                variables?: Omit<TVariables, 'session'>;
            } = {},
        ) => {
            const session = getSession(
                client,
                sessionId,
                sessionTypename,
                sessionKeyQuery,
                nestedSessionKeys,
            );

            const variables = (_.merge({}, opts.variables, {
                session,
            }) as unknown) as TVariables;

            return lazyQuery({
                ...opts,
                variables,
            });
        },
        [lazyQuery, sessionId],
    ) as (
        options?: Partial<
            Omit<LazyQueryHookOptions<TData, TVariables>, 'variables'>
        > & {
            variables?: Omit<TVariables, 'session'>;
        },
    ) => Promise<QueryResult<TData, TVariables>>;

    const refetchWithSession = React.useCallback(
        (vars: Omit<TVariables, 'session'>) => {
            // Refetch is only available after the first fetch
            if (!refetch) {
                return Promise.resolve();
            }

            const session = getSession(
                client,
                sessionId,
                sessionTypename,
                sessionKeyQuery,
                nestedSessionKeys,
            );

            const variables = (_.merge({}, vars, {
                session,
            }) as unknown) as TVariables;

            return refetch(variables);
        },
        [refetch],
    );

    return [
        lazyQueryWithSession,
        { refetch: refetchWithSession, ...restState } as LazyQueryResult<
            TData,
            TVariables
        >,
    ];
}
